import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import {
  Plus,
  Search,
  FolderKanban,
  Calendar,
  Users,
  ArrowRight,
  Star,
  Copy,
  Lock,
  Globe,
  Archive,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { Project, Department } from '../../../shared/types.ts';
import { canCreateProject } from '../../../shared/projectPermissions.ts';
import { StatusBadge } from '../common/Badge.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { Modal } from '../common/Modal.tsx';
import { BOARD_THEMES } from './ProjectDetailView.tsx';
import { ExistingProjectMembersModal } from './ExistingProjectMembersModal.tsx';

interface ProjectListViewProps {
  onSelectProject: (projectId: string) => void;
  onOpenCreateProject?: () => void;
}

export const ProjectListView: React.FC<ProjectListViewProps> = ({ onSelectProject }) => {
  const { token, user } = useAuth();
  const { can } = usePermissions();
  const { subscribe } = useRealtime();
  const [projects, setProjects] = useState<Project[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDept, setFilterDept] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'starred' | 'name' | 'dueDate' | 'progress'>('starred');
  const [showArchivedBoards, setShowArchivedBoards] = useState(false);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [activeProjectForMembers, setActiveProjectForMembers] = useState<Project | null>(null);

  // New Project Form state
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [visibility, setVisibility] = useState<'workspace' | 'private' | 'public'>('workspace');
  const [backgroundTheme, setBackgroundTheme] = useState('default');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [color, setColor] = useState('#026fc1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const getAuthHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token || ''}`,
    ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
    ...extra,
  });

  const fetchProjects = async () => {
    if (!token) return;
    try {
      const url = showArchivedBoards ? '/api/projects?includeArchived=true' : '/api/projects';
      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  useEffect(() => {
    fetchProjects();

    if (token) {
      fetch('/api/team/departments', { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => {
          const isDummy = (name?: string) => {
            if (!name) return false;
            const l = name.toLowerCase();
            return l.includes('insuretech') || l.includes('insurance solutions') || l.includes('regulatory compliance') || l.includes('cloud infrastructure');
          };
          setDepartments((d.departments || []).filter((dept: any) => !isDummy(dept.name)));
        });
    }
  }, [token, showArchivedBoards, user?.id]);

  // Real-time synchronization for projects and task metrics
  useEffect(() => {
    const handleProjectMutated = () => {
      fetchProjects();
    };

    window.addEventListener('project-created', handleProjectMutated);
    window.addEventListener('task-created', handleProjectMutated);
    window.addEventListener('task-updated', handleProjectMutated);
    window.addEventListener('task-archived', handleProjectMutated);
    window.addEventListener('task-restored', handleProjectMutated);

    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'project_created' ||
        event.type === 'project_updated' ||
        event.type === 'project_deleted' ||
        event.type === 'task_created' ||
        event.type === 'task_updated' ||
        event.type === 'task_archived' ||
        event.type === 'task_restored' ||
        event.type === 'tasks_imported'
      ) {
        fetchProjects();
      }
    });

    return () => {
      window.removeEventListener('project-created', handleProjectMutated);
      window.removeEventListener('task-created', handleProjectMutated);
      window.removeEventListener('task-updated', handleProjectMutated);
      window.removeEventListener('task-archived', handleProjectMutated);
      window.removeEventListener('task-restored', handleProjectMutated);
      unsubscribe();
    };
  }, [subscribe, token, showArchivedBoards]);

  const handleToggleStar = async (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    if (!token) return;
    const optimistic = !project.isStarred;
    setProjects((prev) =>
      prev.map((p) => (p.id === project.id ? { ...p, isStarred: optimistic } : p))
    );
    try {
      const res = await fetch(`/api/projects/${project.id}/star`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setProjects((prev) =>
          prev.map((p) => (p.id === project.id ? { ...p, isStarred: data.isStarred } : p))
        );
        window.dispatchEvent(new CustomEvent('project-updated', { detail: data.project }));
      }
    } catch (err) {
      console.error('Failed to toggle star:', err);
    }
  };

  const handleDuplicateBoard = async (e: React.MouseEvent, project: Project) => {
    e.stopPropagation();
    if (!token) return;
    try {
      const res = await fetch(`/api/projects/${project.id}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ name: `${project.name} (Copy)` }),
      });
      if (res.ok) {
        const data = await res.json();
        fetchProjects();
        if (data.project) {
          window.dispatchEvent(new CustomEvent('project-created', { detail: data.project }));
        }
      }
    } catch (err) {
      console.error('Failed to duplicate board:', err);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !key.trim() || !token) return;

    setIsSubmitting(true);
    setCreateError(null);
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: name.trim(),
          key: key.trim().toUpperCase(),
          description: description.trim(),
          departmentId: departmentId || null,
          priority,
          visibility,
          backgroundTheme,
          startDate,
          dueDate: dueDate || new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
          color,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setName('');
        setKey('');
        setDescription('');
        setIsCreateOpen(false);
        fetchProjects();
        if (data.project) {
          window.dispatchEvent(new CustomEvent('project-created', { detail: data.project }));
          onSelectProject(data.project.id);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setCreateError(err.error || 'Failed to create project.');
      }
    } catch (err) {
      console.error('Failed to create project:', err);
      setCreateError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveProjectMember = async (projectId: string, memberId: string) => {
    if (!token) return;
    const res = await fetch(`/api/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (res.ok) {
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, members: (p.members || []).filter((m) => m.id !== memberId) }
            : p
        )
      );
      setActiveProjectForMembers((prev) =>
        prev && prev.id === projectId
          ? { ...prev, members: (prev.members || []).filter((m) => m.id !== memberId) }
          : prev
      );
    } else {
      const errD = await res.json().catch(() => ({}));
      throw new Error(errD.error || 'Failed to remove member from project.');
    }
  };

  const filtered = projects
    .filter((p) => {
      if (!showArchivedBoards && p.isArchived) return false;
      if (filterStatus !== 'all') {
        const s = p.status;
        if (filterStatus === 'todo' && s !== 'todo' && s !== 'planning') return false;
        if (filterStatus === 'in_progress' && s !== 'in_progress' && s !== 'active') return false;
        if (filterStatus === 'in_review' && s !== 'in_review') return false;
        if (filterStatus === 'done' && s !== 'done' && s !== 'completed') return false;
      }
      if (filterDept !== 'all' && p.departmentId !== filterDept) return false;
      if (search.trim()) {
        const term = search.toLowerCase().trim();
        return (
          (p.name || '').toLowerCase().includes(term) ||
          (p.key && p.key.toLowerCase().includes(term)) ||
          (p.code && p.code.toLowerCase().includes(term)) ||
          (p.description && p.description.toLowerCase().includes(term))
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'dueDate') return (a.dueDate || '9999').localeCompare(b.dueDate || '9999');
      if (sortBy === 'progress') return (b.progressPercent || 0) - (a.progressPercent || 0);
      // default 'starred'
      if (a.isStarred && !b.isStarred) return -1;
      if (!a.isStarred && b.isStarred) return 1;
      return 0;
    });

  const starredBoards = filtered.filter((p) => p.isStarred);
  const canCreate = canCreateProject(user?.role);

  const renderBoardCard = (project: Project) => (
    <div
      key={project.id}
      onClick={() => onSelectProject(project.id)}
      className="bg-white dark:bg-surface-900 rounded-2xl p-5 border border-surface-200 dark:border-surface-800 shadow-card hover:shadow-elevated hover:border-brand-500/40 cursor-pointer transition-all flex flex-col justify-between group relative overflow-hidden"
    >
      {/* Top Color Accent Strip */}
      <div
        className="absolute top-0 left-0 right-0 h-1.5 transition-all group-hover:h-2"
        style={{ backgroundColor: project.color || '#026fc1' }}
      />

      <div className="pt-1">
        {/* Header: Color, Key, Visibility & Actions */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: project.color || '#026fc1' }}
            />
            <span className="font-mono text-xs font-bold text-brand-600 dark:text-brand-400 px-2 py-0.5 rounded bg-brand-500/10">
              {project.code || project.key}
            </span>
            {project.visibility === 'private' ? (
              <span title="Private Board" className="text-amber-500">
                <Lock className="w-3.5 h-3.5" />
              </span>
            ) : project.visibility === 'public' ? (
              <span title="Public Board" className="text-emerald-500">
                <Globe className="w-3.5 h-3.5" />
              </span>
            ) : null}
            {project.departmentName && (
              <span className="text-[10px] text-surface-400 hidden sm:inline">
                • {project.departmentName}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveProjectForMembers(project);
              }}
              className="p-1.5 rounded-lg text-surface-400 hover:text-brand-600 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
              title={`View existing members of ${project.name}`}
            >
              <Users className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => handleDuplicateBoard(e, project)}
              className="p-1.5 rounded-lg text-surface-400 hover:text-brand-600 hover:bg-surface-100 dark:hover:bg-surface-800 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Duplicate Project"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => handleToggleStar(e, project)}
              className={`p-1.5 rounded-lg transition-colors ${
                project.isStarred
                  ? 'text-amber-400 hover:bg-amber-500/10'
                  : 'text-surface-300 hover:text-amber-400 hover:bg-surface-100 dark:hover:bg-surface-800'
              }`}
              title={project.isStarred ? 'Unstar Project' : 'Star Project'}
            >
              <Star
                className={`w-3.5 h-3.5 ${
                  project.isStarred ? 'fill-amber-400 text-amber-400' : ''
                }`}
              />
            </button>
            <StatusBadge status={project.status} size="sm" />
          </div>
        </div>

        {/* Title & Description */}
        <h3 className="font-bold text-sm text-surface-900 dark:text-surface-100 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors line-clamp-1">
          {project.name}
        </h3>
        <p className="text-xs text-surface-500 mt-1 line-clamp-2 leading-relaxed">
          {project.description}
        </p>
      </div>

      {/* Bottom Meta */}
      <div className="mt-6 pt-4 border-t border-surface-100 dark:border-surface-800/80 space-y-3">
        {/* Progress bar */}
        <div>
          <div className="flex justify-between text-[11px] mb-1">
            <span className="text-surface-400">Progress</span>
            <span className="font-semibold text-surface-700 dark:text-surface-300">
              {project.progressPercent || 0}% ({project.completedTaskCount || 0}/{project.taskCount || 0} cards)
            </span>
          </div>
          <div className="w-full bg-surface-100 dark:bg-surface-800 h-1.5 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${project.progressPercent || 0}%`,
                backgroundColor: project.color || '#026fc1',
              }}
            />
          </div>
        </div>

        {/* Dates & Members */}
        <div className="flex items-center justify-between text-xs text-surface-500 pt-1">
          <span className="flex items-center text-[11px]">
            <Calendar className="w-3.5 h-3.5 mr-1" />
            Due: {project.dueDate || '—'}
          </span>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveProjectForMembers(project);
              }}
              className="flex items-center space-x-1.5 px-2 py-1 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-300 border border-surface-200/80 dark:border-surface-700/80 hover:border-brand-500/40 transition-all cursor-pointer group/members"
              title={`View ${project.members?.length || 0} project members in modal`}
            >
              {project.members && project.members.length > 0 ? (
                <div className="flex items-center -space-x-1.5">
                  {project.members.slice(0, 3).map((m) => (
                    <Avatar key={m.id} src={m.avatarUrl} name={m.fullName} size="xs" />
                  ))}
                  {project.members.length > 3 && (
                    <span className="w-4 h-4 rounded-full bg-surface-200 dark:bg-surface-700 text-surface-600 dark:text-surface-300 text-[9px] font-bold flex items-center justify-center">
                      +{project.members.length - 3}
                    </span>
                  )}
                </div>
              ) : (
                <Users className="w-3.5 h-3.5 text-surface-400 group-hover/members:text-brand-600" />
              )}
              <span className="text-[11px] font-semibold text-surface-600 dark:text-surface-300 group-hover/members:text-brand-600">
                {project.members?.length || 0}
              </span>
            </button>
            <ArrowRight className="w-3.5 h-3.5 text-surface-300 group-hover:text-brand-600 group-hover:translate-x-0.5 transition-all" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Top Banner & Action */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-2xs">
        <h2 className="text-sm sm:text-base font-extrabold text-surface-900 dark:text-surface-100 tracking-tight uppercase">
          Projects Portfolio
        </h2>

        {canCreate && (
          <button
            onClick={() => setIsCreateOpen(true)}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-[11px] font-bold shadow-sm flex items-center space-x-1 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Create New Project</span>
          </button>
        )}
      </div>

      {/* Filter and Search Bar */}
      <div className="px-3.5 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-2xs flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            aria-label="Sort Projects"
            className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none font-medium"
          >
            <option value="starred">Sort: Starred First</option>
            <option value="name">Sort: Alphabetical</option>
            <option value="dueDate">Sort: Due Date</option>
            <option value="progress">Sort: Progress</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label="Filter by Stage"
            className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none font-medium"
          >
            <option value="all">All Stages</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="in_review">In Review</option>
            <option value="done">Completed</option>
          </select>

          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            aria-label="Filter by Department"
            className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none"
          >
            <option value="all">All Departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setShowArchivedBoards((prev) => !prev)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold border flex items-center gap-1.5 transition-colors ${
              showArchivedBoards
                ? 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-400'
                : 'bg-surface-50 dark:bg-surface-800 border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400'
            }`}
          >
            <Archive className="w-3.5 h-3.5" />
            <span>Archived Projects</span>
          </button>
        </div>
      </div>

      {/* Starred Projects Section */}
      {starredBoards.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-surface-600 dark:text-surface-300">
              Starred Projects ({starredBoards.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {starredBoards.map((p) => renderBoardCard(p))}
          </div>
        </div>
      )}

      {/* All Projects Grid */}
      <div className="space-y-3">
        {starredBoards.length > 0 && (
          <div className="flex items-center space-x-2">
            <FolderKanban className="w-4 h-4 text-brand-500" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-surface-600 dark:text-surface-300">
              All Projects ({filtered.length})
            </h3>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Create New Project Tile */}
          {canCreate && (
            <div
              onClick={() => setIsCreateOpen(true)}
              className="min-h-[220px] rounded-2xl p-6 border-2 border-dashed border-surface-300 dark:border-surface-700 hover:border-brand-500 dark:hover:border-brand-400 bg-surface-50/70 hover:bg-brand-50/20 dark:bg-surface-900/40 dark:hover:bg-brand-950/20 cursor-pointer transition-all duration-200 flex flex-col items-center justify-center text-center group"
            >
              <div className="w-12 h-12 rounded-2xl bg-white dark:bg-surface-800 shadow-sm border border-surface-200 dark:border-surface-700 flex items-center justify-center text-surface-400 group-hover:text-brand-600 group-hover:border-brand-300 dark:group-hover:border-brand-700 transition-all mb-3 group-hover:scale-110">
                <Plus className="w-6 h-6 stroke-[2.5]" />
              </div>
              <h3 className="font-bold text-sm text-surface-800 dark:text-surface-200 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                Create New Project
              </h3>
              <p className="text-[11px] text-surface-400 mt-1 max-w-[200px]">
                Start a new initiative, milestone schedule, or client deliverable
              </p>
            </div>
          )}

          {filtered.map((project) => renderBoardCard(project))}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="py-16 text-center text-surface-400 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800">
          <FolderKanban className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm font-semibold text-surface-600 dark:text-surface-400">No projects found</p>
          <p className="text-xs text-surface-400 mt-1">Try changing the filters or create a new project above.</p>
        </div>
      )}

      {/* Create Board Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          setCreateError(null);
        }}
        title="Create New Project"
        maxWidth="lg"
      >
        <form onSubmit={handleCreateProject} className="space-y-4 text-xs">
          {createError && (
            <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400 text-[11px] font-semibold">
              {createError}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Project Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. ERP Modernization, Marketing Launch, Mobile App"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!key) {
                    const autoKey = e.target.value
                      .replace(/[^a-zA-Z0-9]/g, '')
                      .slice(0, 5)
                      .toUpperCase();
                    setKey(autoKey);
                  }
                }}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>
            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Prefix Key <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="ERP"
                maxLength={6}
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase())}
                className="w-full font-mono uppercase px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Description & Objectives
            </label>
            <textarea
              rows={2}
              placeholder="High-level project scope, milestone goals, and target deliverables..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Workspace / Dept
              </label>
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              >
                <option value="">General Space</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Visibility
              </label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as any)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              >
                <option value="workspace">Workspace</option>
                <option value="private">Private</option>
                <option value="public">Public</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              >
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Target Due Date
              </label>
              <input
                type="date"
                required
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1.5">
              Board Background Theme
            </label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(BOARD_THEMES).map(([k, theme]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setBackgroundTheme(k)}
                  className={`p-2 rounded-xl border text-left flex items-center gap-2 transition-all ${
                    backgroundTheme === k
                      ? 'border-brand-500 ring-2 ring-brand-500/20 font-bold'
                      : 'border-surface-200 dark:border-surface-700'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-md shrink-0 ${theme.preview}`} />
                  <span className="truncate text-[11px]">{theme.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Board Accent Color
            </label>
            <div className="flex items-center space-x-2.5">
              {[
                { hex: '#026fc1', name: 'Classic Blue' },
                { hex: '#4f46e5', name: 'Indigo' },
                { hex: '#059669', name: 'Emerald' },
                { hex: '#d97706', name: 'Amber' },
                { hex: '#e11d48', name: 'Rose' },
                { hex: '#7c3aed', name: 'Purple' },
                { hex: '#0d9488', name: 'Teal' },
                { hex: '#334155', name: 'Slate' },
              ].map((c) => (
                <button
                  type="button"
                  key={c.hex}
                  title={c.name}
                  onClick={() => setColor(c.hex)}
                  className={`w-7 h-7 rounded-xl transition-all shadow-subtle ${
                    color === c.hex ? 'scale-110 ring-2 ring-offset-2 ring-brand-500' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsCreateOpen(false)}
              className="px-4 py-2 font-medium text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || !key.trim()}
              className="px-5 py-2 font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm disabled:opacity-50"
            >
              {isSubmitting ? 'Creating Project...' : 'Create Project'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Existing Project Members Modal */}
      {activeProjectForMembers && (
        <ExistingProjectMembersModal
          isOpen={Boolean(activeProjectForMembers)}
          onClose={() => setActiveProjectForMembers(null)}
          projectName={activeProjectForMembers.name}
          members={activeProjectForMembers.members || []}
          canManageMembers={can('project.members') || activeProjectForMembers.ownerId === user?.id}
          onFilterByMember={() => {
            onSelectProject(activeProjectForMembers.id);
            setActiveProjectForMembers(null);
          }}
          onAddMemberClick={() => {
            onSelectProject(activeProjectForMembers.id);
            setActiveProjectForMembers(null);
          }}
          onRemoveMember={(memberId) =>
            handleRemoveProjectMember(activeProjectForMembers.id, memberId)
          }
        />
      )}
    </div>
  );
};
